import { notAvailable } from 'core/common/constants';
import { normalizeError } from 'core/common/errors';
import { ATTRS_ANALYTICS_FUNNEL_NAME, SpanName } from 'core/common/observability/entities';
import { AppThunk } from 'core/common/store';
import { requireEmailConsentFeature } from 'core/feature-flags/config';
import { CreateOrderParams } from 'core/payments/entities';
import { updateOrderId } from 'core/payments/store/actions';
import { UserCreateOrder } from 'core/user/entities';
import { getUserLocation, getUserResponses } from '../../selectors';
import { getUtmTags } from '../../userUtmTags';
import { getIsUserTester, getUserInformation } from '../selectors';
import { updateUserInformation } from './updateUserInformation';

export const createOrder =
  ({ email, offerName, quizName, analyticsIds, emailFlow, reports }: UserCreateOrder): AppThunk =>
  async (dispatch, getState, context) => {
    const {
      purchaseService,
      analyticsService,
      observabilitySystem,
      heatmapRecordingService,
      errorMonitoringService,
      loggingService,
      featureFlags,
      httpClient,
    } = context;
    const userInformation = getUserInformation(getState());
    const isUserTester = getIsUserTester(getState());
    const userResponses = getUserResponses(getState());
    const mediaSources = analyticsService.getAttributionParameters();
    const utmTags = getUtmTags(getState());
    const sessionId = analyticsService.getSessionId();
    const deviceId = analyticsService.getDeviceId();

    const isEmailConsentRequired = featureFlags.isFeatureOn(requireEmailConsentFeature);
    const emailConsent = !isEmailConsentRequired;

    const index = email.indexOf('@');
    const name = userInformation.name || email.slice(0, index);

    const hasFacebookPixelIds = analyticsIds.facebook.length > 0;
    const hasTikTokPixelIds = analyticsIds.tikTok.length > 0;
    const hasAnalyticsIds = hasTikTokPixelIds || hasFacebookPixelIds;

    const userResponsesValues = Object.values(userResponses);

    const data: CreateOrderParams = {
      order_data: {
        subjects: [
          {
            assigned_to: 'partner_1',
            birth_place: {
              place: userInformation.location || 'Greenwich, Greater London, United Kingdom',
              longitude: userInformation.place[0] || 0.05,
              latitude: userInformation.place[1] || 51.45,
            },
            gender: userInformation.gender,
            birth_date: userInformation.date || '1990-01-01',
            birth_time: userInformation.time,
          },
          {
            assigned_to: 'partner_2',
            birth_place: {
              place: userInformation.partnerLocation || 'Greenwich, Greater London, United Kingdom',
              latitude: userInformation.partnerPlace[0] || 0.05,
              longitude: userInformation.partnerPlace[1] || 51.45,
            },
            gender: userInformation.partnerGender || 'male',
            birth_date: userInformation.partnerDate || '1990-01-01',
            birth_time: userInformation.partnerTime,
          },
        ],
        reports,
      },
      quiz: quizName,
      media_sources: {
        ...mediaSources,
        ...utmTags,
      },
      email,
      offer_name: offerName,
      project: 'Nebula',
      customer_settings: {
        email_consent: emailConsent,
        do_not_sell_data: false,
        report_type: userInformation.relationshipType,
        ...(hasAnalyticsIds
          ? {
              pixels: {
                ...(hasFacebookPixelIds ? { facebook: analyticsIds.facebook } : {}),
                ...(hasTikTokPixelIds ? { tik_tok: analyticsIds.tikTok } : {}),
              },
            }
          : {}),
        emailFlow,
      },
      quiz_data: {
        email,
        name,
        birth_time: userInformation.time || '12:00:00',
        gender: userInformation.gender,
        birth_date: userInformation.date || '1990-01-01',
        partnerName: userInformation.partnerName,
        partnerTime: userInformation.partnerTime || '12:00:00',
        partner_gender: userInformation.partnerGender || 'male',
        partner_birth_date: userInformation.partnerDate || '1990-01-01',
        partner_birth_place: userInformation.partnerLocation,
        flow_choice: userInformation.relationshipType,
        flow_goal:
          userInformation.palmReadingGoal ||
          userInformation.wpGoal ||
          userInformation.relationshipGoal ||
          userInformation.mainGoal,
        wpGoal: userInformation.wpGoal,
        relationship: userInformation.relationship,
        mainPain: userInformation.mainPain,
        mainGoal: userInformation.mainGoal,
        challenges: userInformation.challenges,
        topics: userInformation.topics,
        relationshipGoal: userInformation.relationshipGoal,
        palmReadingGoal: userInformation.palmReadingGoal,
        astrology_knowledge: userInformation.astrologyKnowledge,
        traits_relate_to_statement: userInformation.relateToStatement,
        traits_most_important: userInformation.mostImportant,
        relationshipStatus: userInformation.relationshipStatus,
        relationship_not_good_enough: userInformation.goodEnough,
        relationship_ideal_date: userInformation.idealDate,
        relationship_future_partner: userInformation.futurePartner,
        relationship_ideal_partner: userInformation.idealPartner,
        relationship_how_to_make_decisions: userInformation.decisions,
        palmistry_scan_id: userInformation.scan ? userInformation.scan.photoId : '',
        scan: userInformation.scan,
        ancestor_data: userInformation.ancestor_data,
        nature_data: userInformation.nature_data,
        tradition_data: userInformation.tradition_data,
        crowdComfort_data: userInformation.crowdComfort_data,
        intuitiveHealing_data: userInformation.intuitiveHealing_data,
        moonPhase_data: userInformation.moonPhase_data,
        worryPartnerLeaves: userInformation.worryPartnerLeaves,
        archetype: userInformation.archetype,
        partnerZodiac: userInformation.partnerZodiac,
        partnerGender: userInformation.partnerGender,
        // TODO temporary solution. Remove after fix backend
        emailConsent: emailConsent,
        responses: userResponsesValues,
      },
      sessionId,
      deviceId,
    };

    const userLocation = getUserLocation(getState());

    const spanAttributes = {
      [ATTRS_ANALYTICS_FUNNEL_NAME]: quizName,
    };

    const span = observabilitySystem.startSpan(SpanName.CREATE_ORDER);
    span.setAttributes(spanAttributes);

    span.addEvent('Pending');

    try {
      const { orderId, userId, nebulaId } = await purchaseService.createOrder(data);

      span.setUserId(userId);

      analyticsService.setIsUserTester(isUserTester);
      analyticsService.addEmail(email, userId);
      analyticsService.addUserId(userId);
      analyticsService.lead(orderId);
      analyticsService.updateUserProperties({ email_consent: emailConsent });
      heatmapRecordingService.identify(email);

      httpClient.setHeaders({
        User: nebulaId.toString(),
      });

      dispatch(updateUserInformation('orderData', data.order_data));
      dispatch(updateUserInformation('email', email));
      dispatch(updateOrderId(orderId));

      span.addEvent('Succeed');
      span.succeed();
    } catch (err) {
      const error = normalizeError(err);
      loggingService.error(error);

      errorMonitoringService.captureError(error, {
        tags: {
          email,
          quizName,
          emailFlow,
          get_params: location.search || notAvailable,
          error_message: error.message,
          error_reason: 'create_order',
          ...userLocation,
        },
      });

      span.addEvent('Failed');
      span.fail(error);
    }
  };
